import { computed, shallowRef } from 'vue'

/**
 * Whether, and how, this browser can add the app to the home screen.
 *
 * Module-scoped rather than per-component for a reason that is easy to miss:
 * `beforeinstallprompt` fires once per page load, and it can fire before any
 * component has mounted. A listener attached in `onMounted` would miss it on a
 * cold load and the install row would simply never appear.
 *
 * The three states are not symmetric, because the platforms are not:
 *
 *   Chromium  fires the event, and we can trigger the real install sheet
 *   iOS       fires nothing, and offers no API — only an instruction is honest
 *   others    look exactly like "Chromium before the criteria are met"
 *
 * That last row is why there is no fallback button. A control that might do
 * nothing when tapped is worse than no control.
 */

/**
 * Not in lib.dom: `beforeinstallprompt` is a Chromium extension to the platform,
 * so the shape has to be declared here to be usable at all.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent
  }
}

const promptEvent = shallowRef<BeforeInstallPromptEvent | null>(null)
const installed = shallowRef(false)
const iosCapable = shallowRef(false)

/**
 * `display-mode: standalone` covers every browser that honours the manifest;
 * `navigator.standalone` is the iOS-only equivalent, since iOS launches a
 * home-screen app without reporting the display mode.
 */
function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const byDisplayMode = window.matchMedia?.('(display-mode: standalone)').matches ?? false
  const byIosFlag = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return byDisplayMode || byIosFlag
}

/**
 * iPadOS 13+ reports itself as `MacIntel`, so the user agent alone under-detects
 * exactly the devices most likely to install a transit app. The touch-point
 * check is what separates an iPad from a desktop Mac.
 */
function detectIos(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/iPad|iPhone|iPod/.test(ua)) return true
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
}

function detect() {
  installed.value = detectStandalone()
  iosCapable.value = detectIos()
}

detect()

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Without preventDefault Chrome may show its own mini-infobar, so the app
    // would offer the install twice in two different places.
    event.preventDefault()
    promptEvent.value = event
  })

  // Also fires when the user installs through the browser's own menu rather
  // than our row, which is the case a naive implementation leaves stale.
  window.addEventListener('appinstalled', () => {
    promptEvent.value = null
    installed.value = true
  })
}

const canInstall = computed(() => !installed.value && promptEvent.value !== null)
const showIosHint = computed(
  () => !installed.value && iosCapable.value && promptEvent.value === null,
)

export function useInstallPrompt() {
  return {
    canInstall,
    showIosHint,
    async install() {
      const event = promptEvent.value
      if (!event) return
      // Cleared BEFORE awaiting, not after: the browser discards the event once
      // prompted, so a second tap arriving during the await would call a dead
      // `prompt()`.
      promptEvent.value = null
      await event.prompt()
    },
  }
}

/**
 * Test-only. The state above is module-scoped, so it outlives a test the way it
 * outlives a component — and the platform detection runs at import time, before
 * a test could stub `matchMedia` or the user agent. Re-running it here is what
 * lets each case describe its own platform.
 */
export function resetInstallPrompt(): void {
  promptEvent.value = null
  detect()
}
